import useQuerySelect from '@/hooks/useQuerySelect';
import { useQuery } from '@tanstack/react-query';
import { SelectProps } from 'antd';
import { createElement } from 'react';
import { getCustomerGroups } from '../services/customer';
import {
	MasterDataCondition,
	getAddressMasterData,
	getBouwheerMasterData,
	getBrandMasterData,
	getBrandTypeMasterData,
	getCommercialBankMasterData,
	getCorporateBankMasterData,
	getCountryMasterData,
	getDivisionMasterData,
	getIndustryMasterData,
	getMandiriBranchesMasterData,
	getMasterBankData,
	getMasterData,
	getMasterLookupData,
	getMasterSupplierData,
	getSupplierDealerMasterData,
	searchAsset,
} from '../services/master-data';
import { searchAssetBrand } from '../services/master-data';
import { getKaroseriCompanyList } from '../services/master-data';
import { getProductList } from '../services/master-data';

export const useSelectOrderType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'order-type'],
		queryFn: async () => {
			const res = await getMasterData('order-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectSegmentMarket = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'segment-market'],
		queryFn: async () => {
			const res = await getMasterData('segment-market');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectMasterBank = (enabled = false) => {
	return useQuerySelect({
		enabled,
		queryKey: ['getMasterBankData'],
		queryFn: async (pageNumber, keys) => {
			const res = await getMasterBankData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'bank-name',
					value: keys[1],
				},
			});
			return res.data.items.map((data) => {
				// Ensure the label is always a string, handle cases where value might be a number
				const bankName =
					typeof data.value === 'string'
						? data.value
						: String(data.value || '');

				return {
					label: bankName,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectSourceOrder = (
	segmentMarketId: number | undefined | null,
) => {
	return useQuerySelect({
		queryKey: [
			'getMasterData',
			'source-order-detail',
			segmentMarketId,
		] as const,
		queryFn: async (_, [, , segmentMarketId]) => {
			if (typeof segmentMarketId === 'undefined') return [];

			const res = await getMasterData(
				'source-order-detail',
				segmentMarketId
					? {
							parentSlug: 'segment-market',
							parentDataId: segmentMarketId,
						}
					: null,
			);

			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectIdentityType = (
	shareholderTypeId: number | undefined,
) => {
	return useQuerySelect({
		queryKey: [
			'getMasterData',
			'identity-card-type',
			shareholderTypeId,
		] as const,
		queryFn: async (_, [, , shareholderTypeId]) => {
			if (!shareholderTypeId) return [];

			const res = await getMasterData('identity-card-type', {
				parentSlug: 'shareholder-type',
				parentDataId: shareholderTypeId,
			});

			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectCommercialBank = () => {
	return useQuerySelect({
		queryKey: ['getCommercialBankMasterData'],
		queryFn: async (pageNumber, keys) => {
			const res = await getCommercialBankMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'bank-name',
					value: keys[1],
				},
			});
			return res.data.items.map((data) => {
				// Ensure the label is always a string, handle cases where value might be a number
				const bankName =
					typeof data.value === 'string'
						? data.value
						: String(data.value || '');

				return {
					label: bankName,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectSupplierDealer = () => {
	return useQuerySelect({
		queryKey: ['getSupplierDealerMasterData'],
		queryFn: async (pageNumber, keys) => {
			const res = await getSupplierDealerMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'name',
					value: keys[1],
				},
			});
			return res.data.items.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectCorporateBank = () => {
	return useQuerySelect({
		queryKey: ['getCorporateBankMasterData'],
		queryFn: async (pageNumber, keys) => {
			const res = await getCorporateBankMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'bank-name',
					value: keys[1],
				},
			});
			return res.data.items.map((data) => {
				// Ensure the label is always a string, handle cases where value might be a number
				const bankName =
					typeof data.value === 'string'
						? data.value
						: String(data.value || '');

				return {
					label: bankName,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectMandiriBranches = () => {
	return useQuerySelect({
		queryKey: ['getMandiriBranchesMasterData'],
		queryFn: async (pageNumber, keys) => {
			const res = await getMandiriBranchesMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'branch-name',
					value: keys[1],
				},
			});
			return res.data.items.map((data) => {
				return {
					label: data.branchName,
					value: data.branchId,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectGeneralPosition = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'general-position'],
		queryFn: async () => {
			const res = await getMasterData('general-position');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectSlikCheckingStatus = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'slik-checking-status'],
		queryFn: async () => {
			const res = await getMasterData('slik-checking-status');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectPlafonType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'plafon-type'],
		queryFn: async () => {
			const res = await getMasterData('plafon-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectFinancingLimitCharacteristic = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'financing-limit-characteristic'],
		queryFn: async () => {
			const res = await getMasterData('financing-limit-characteristic');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectFinancingType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'financing-type'],
		queryFn: async () => {
			const res = await getMasterData('financing-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectFinancialReportAuditStatus = (enabled = false) => {
	return useQuerySelect({
		enabled,
		queryKey: ['getMasterData', 'financial-report-audit-status'],
		queryFn: async () => {
			const res = await getMasterData('financial-report-audit-status');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectBankStatementType = (enabled = false) => {
	return useQuerySelect({
		enabled,
		queryKey: ['getMasterData', 'bank-statement-account-type'],
		queryFn: async () => {
			const res = await getMasterData('bank-statement-account-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectFinancialReportPeriodType = (enabled = false) => {
	return useQuerySelect({
		enabled,
		queryKey: ['getMasterData', 'financial-report-period-type'],
		queryFn: async () => {
			const res = await getMasterData('financial-report-period-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectFinancialReportPeriodTypeDetail = (
	condition?: MasterDataCondition,
	enabled = false,
) => {
	return useQuerySelect({
		enabled,
		queryKey: [
			'getMasterData',
			'financial-report-period-type-detail',
			condition,
		] as const,
		queryFn: async (_, [, , parent]) => {
			// If no parent condition, get all period type details
			const res = await getMasterData(
				'financial-report-period-type-detail',
				parent || null,
			);
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useQueryFinancingTypeDetails = (financingTypeId?: number) => {
	return useQuery({
		queryKey: [
			'getMasterData',
			'financing-type-detail',
			financingTypeId,
		] as const,
		queryFn: async ({ queryKey }) => {
			if (!queryKey[2]) return [];

			const res = await getMasterData('financing-type-detail', {
				parentSlug: 'financing-type',
				parentDataId: queryKey[2],
			});

			return res.data;
		},
	});
};

export const useQueryFinancingObject = () => {
	return useQuery({
		queryKey: ['getMasterData', 'financing-object'] as const,
		queryFn: async () => {
			const { data } = await getMasterData('financing-object');
			return data;
		},
	});
};
export const useQueryFinancingType = () => {
	return useQuery({
		queryKey: ['getMasterData', 'financing-type'] as const,
		queryFn: async () => {
			const { data } = await getMasterData('financing-type');
			return data;
		},
	});
};

export const useSelectCompanyType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'company-type'],
		queryFn: async () => {
			const { data } = await getMasterData('company-type');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectBusinessEntity = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'business-entity'],
		queryFn: async () => {
			const { data } = await getMasterData('business-entity');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectCustomerPicPosition = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'customer-pic-position'],
		queryFn: async () => {
			const { data } = await getMasterData('customer-pic-position');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectOfficeOwnershipStatus = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'office-ownership-status'],
		queryFn: async () => {
			const { data } = await getMasterData('office-ownership-status');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectSalesMethod = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'sales-method'],
		queryFn: async () => {
			const { data } = await getMasterData('sales-method');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectPaymentMethod = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'payment-method'],
		queryFn: async () => {
			const { data } = await getMasterData('payment-method');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectInventoryItemType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'inventory-item-type'],
		queryFn: async () => {
			const { data } = await getMasterData('inventory-item-type');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectMaterialOwnershipType = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'material-ownership-type'],
		queryFn: async () => {
			const { data } = await getMasterData('material-ownership-type');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectRejectCategory = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'reject-category'],
		queryFn: async () => {
			const { data } = await getMasterData('reject-category');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: false,
	});
};

export const useSelectRejectCustomer = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'reject-customer'],
		queryFn: async () => {
			const { data } = await getMasterData('reject-customer');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: false,
	});
};

export const useSelectSlikStatus = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'slik-status'],
		queryFn: async () => {
			const { data } = await getMasterData('slik-status');
			return data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectCustomerGroups = () => {
	return useQuerySelect({
		queryKey: ['getCustomerGroups'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getCustomerGroups({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'group-name',
					value: keys[1],
				},
			});
			return data.items.map((data) => {
				return {
					label: data.groupName,
					value: data.groupId,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectCitizenship = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'citizenship-type'],
		queryFn: async () => {
			const res = await getMasterData('citizenship-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectManagementPosition = () => {
	return useQuerySelect({
		infinite: false,
		queryKey: ['getMasterData', 'company-management-position'],
		queryFn: async () => {
			const res = await getMasterData('company-management-position');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectShareholderType = () => {
	return useQuerySelect({
		queryKey: ['getCountryMasterData', 'shareholder-type'],
		queryFn: async () => {
			const res = await getMasterData('shareholder-type');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectCountry = () => {
	return useQuerySelect({
		queryKey: ['getCountryMasterData'],
		queryFn: async (page, key) => {
			const res = await getCountryMasterData(page, key[1]);
			return res.data.items.map((data) => {
				return {
					label: data.name,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectGeneralStatus = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'general-status'],
		queryFn: async () => {
			const res = await getMasterData('general-status');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

export const useSelectAddress = () => {
	const selectProps = useQuerySelect({
		queryKey: ['getAddressMasterData'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getAddressMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'postal',
					value: keys[1],
				},
			});

			return data.items.map((data) => {
				return {
					label: data.postalCode,
					value: data.postalId,
					caption: `${data.locationName},${data.districtName},${data.cityName}`,
					data,
				};
			});
		},
		infinite: true,
		minSearchLength: 3,
	});

	return {
		...selectProps,
		optionRender: (address) => {
			return createElement('div', {}, [
				createElement(
					'div',
					{ style: { whitespace: 'nowrap' } },
					address.label,
				),
				createElement(
					'div',
					{
						style: {
							whiteSpace: 'wrap',
							fontSize: 12,
							color: '#bababa',
						},
					},
					address.data.caption,
				),
			]);
		},
	} as SelectProps;
};

type LookupOption = {
	label: string;
	value: string;
	caption?: string;
	data: Record<string, unknown>;
};

type LookupTransformer = (item: Record<string, unknown>) => LookupOption;

type EndpointLookupConfig = {
	/** API search "by" parameter. Defaults to 'name' when unspecified. */
	searchBy?: string;
	/** Map raw response item → LookupOption. */
	transform: LookupTransformer;
	/** Whether the option list should show a caption row beneath the label. */
	hasCaption?: boolean;
	/** Minimum keyword length before a search request is sent. */
	minSearchLength?: number;
};

const defaultLookupConfig: EndpointLookupConfig = {
	searchBy: 'name',
	transform: (item) => ({
		label: String(item.value ?? ''),
		value: String(item.value ?? ''),
		data: item,
	}),
};

const ENDPOINT_LOOKUP_CONFIG: Record<string, EndpointLookupConfig> = {
	'/master-city/get-list': {
		transform: (item) => ({
			label: String(item.name ?? ''),
			value: String(item.name ?? ''),
			data: item,
		}),
	},
	'/master-country/get-list': {
		transform: (item) => ({
			label: String(item.name ?? ''),
			value: String(item.name ?? ''),
			data: item,
		}),
	},
	// Address lookup ships with custom search-by + caption display. Consumers (e.g.
	// LookupSelect in QCAccordionForm) read `data.locationName/districtName/cityName`
	// to back-fill sibling kelurahan/kecamatan/kota fields after selection.
	'/master-address/get-list': {
		searchBy: 'postal',
		hasCaption: true,
		minSearchLength: 3,
		transform: (item) => ({
			label: String(item.postalCode ?? ''),
			value: String(item.postalCode ?? ''),
			caption: [item.locationName, item.districtName, item.cityName]
				.filter(Boolean)
				.join(', '),
			data: item,
		}),
	},
};

const resolveLookupConfig = (endpoint: string): EndpointLookupConfig => {
	const normalized = endpoint.replace(/^\/api\//, '/');
	return ENDPOINT_LOOKUP_CONFIG[normalized] ?? defaultLookupConfig;
};

export const useSelectByEndpoint = (endpoint: string | null | undefined) => {
	const endpointConfig = endpoint ? resolveLookupConfig(endpoint) : defaultLookupConfig;
	const selectProps = useQuerySelect({
		queryKey: ['lookupByEndpoint', endpoint ?? ''] as const,
		queryFn: async (pageNumber, keys) => {
			const ep = keys[1];
			if (!ep) return [];
			const config = resolveLookupConfig(ep);
			const { data } = await getMasterLookupData(ep, {
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: config.searchBy ?? 'name',
					value: keys[2],
				},
			});
			return data.items.map(config.transform);
		},
		infinite: true,
		enabled: !!endpoint,
		minSearchLength: endpointConfig.minSearchLength,
	});

	// For endpoints that ship a caption (e.g. master-address), render label + caption
	// like useSelectAddress does. Other endpoints get the default Select option rendering.
	if (endpointConfig.hasCaption) {
		return {
			...selectProps,
			optionRender: (option: { data?: LookupOption | { caption?: string }; label?: unknown }) => {
				const inner = (option?.data ?? {}) as LookupOption | { caption?: string };
				const labelText = (option?.label as string | undefined) ?? '';
				const caption = (inner as { caption?: string }).caption ?? '';
				return createElement('div', {}, [
					createElement('div', { style: { whiteSpace: 'nowrap' } }, labelText),
					caption
						? createElement(
								'div',
								{
									style: {
										whiteSpace: 'wrap',
										fontSize: 12,
										color: '#bababa',
									},
								},
								caption,
							)
						: null,
				]);
			},
		} as SelectProps;
	}
	return selectProps;
};

export const useSelectIndustry = () => {
	return useQuerySelect({
		queryKey: ['getIndustryMasterData'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getIndustryMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'sector-level-5',
					value: keys[1],
				},
			});
			return data.items.map((data) => {
				return {
					label: data.sectorLevel5,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectBrand = () => {
	return useQuerySelect({
		queryKey: ['getBrandMasterData'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getBrandMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'brand-name',
					value: keys[1],
				},
			});
			return data.items.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectBrandType = (brandId?: string) => {
	return useQuerySelect({
		queryKey: ['getBrandTypeMasterData', brandId] as const,
		queryFn: async (pageNumber, keys) => {
			const [, brandId, keyword] = keys;
			const { data } = await getBrandTypeMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				...(brandId && {
					filter: {
						by: 'brand-id',
						value: brandId,
					},
				}),
				search: {
					by: 'brand-type-name',
					value: keyword,
				},
			});
			return data.items.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectDivision = () => {
	return useQuerySelect({
		queryKey: ['getDivisionMasterData'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getDivisionMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'name',
					value: keys[1],
				},
			});
			return data.items.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectBouwheer = () => {
	return useQuerySelect({
		queryKey: ['getBouwheerMasterData'],
		queryFn: async (pageNumber, keys) => {
			const { data } = await getBouwheerMasterData({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'name',
					value: keys[1],
				},
			});
			return data.items.map((data) => {
				return {
					label: data.name,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});
};

export const useSelectConfirmationMedia = () => {
	return useQuerySelect({
		queryKey: ['getMasterData', 'order-type'],
		queryFn: async () => {
			const res = await getMasterData('confirmation-media');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});
};

/**
 * Hook to fetch master suppliers for Select components (auto-fetch with search)
 */
export const useSelectMasterSupplier = () => {
	return useQuerySelect({
		queryKey: ['getMasterSupplierData'],
		queryFn: async (_pageNumber, keys) => {
			const res = await getMasterSupplierData(keys[1] || '');
			return res.data.map(
				(item: {
					id: string;
					name: string;
					address: string;
					phoneNo: string;
				}) => ({
					label: item.name,
					value: item.id,
					data: item,
				}),
			);
		},
		infinite: true,
		enabled: true,
	});
};

export const useSelectAsset = (brandName: string) => {
	return useQuerySelect({
		queryKey: ['getAssetList', brandName],
		queryFn: async () => {
			if (!brandName) return [];
			const res = await searchAsset(brandName);
			return res.data.map(
				(item: {
					code: string;
					name: string;
					categoryId: string;
					modelId: string;
					modelName: string;
				}) => ({
					label: `${item.name} (${item.code})`,
					value: item.code,
					data: item,
				}),
			);
		},
		infinite: true,
		enabled: !!brandName,
	});
};

export const useSelectAssetBrand = (assetTypeId?: number) => {
	return useQuerySelect({
		queryKey: ['searchAssetBrand', assetTypeId],
		queryFn: async () => {
			if (!assetTypeId) return [];
			const res = await searchAssetBrand(assetTypeId);
			return res.data.map((item: { id: string; name: string }) => ({
				label: item.name,
				value: item.id,
				data: item,
			}));
		},
		infinite: true,
		enabled: !!assetTypeId,
	});
};

// export const useSelectKaroseriCompany = (supplierName?: string) => {
// 	return useQuerySelect({
// 		queryKey: ['getKaroseriCompanyList', supplierName] as const,
// 		queryFn: async () => {
// 			const res = await getKaroseriCompanyList(supplierName);
// 			return res.data.items.map((item: KaroseriCompany) => ({
// 				label: item.name,
// 				value: item.id,
// 				data: item,
// 			}));
// 		},
// 		infinite: true,
// 		enabled: !!supplierName,
// 	});
// };

export const useSelectKaroseriCompany = () => {
	return useQuerySelect({
		queryKey: ['getKaroseriCompanyList'],
		queryFn: async (_pageNumber, keys) => {
			const res = await getKaroseriCompanyList(keys[1] || '');
			return res.data.map(
				(item: {
					id: string;
					name: string;
					address: string;
					phoneNo: string;
				}) => ({
					label: item.name,
					value: item.id,
					data: item,
				}),
			);
		},
		infinite: true,
		enabled: true,
	});
};

export const useSelectProduct = () => {
	return useQuerySelect({
		queryKey: ['getProductList'],
		queryFn: async (pageNumber, keys) => {
			const res = await getProductList({
				pagination: {
					pageNumber,
					pageSize: 10,
				},
				search: {
					by: 'name',
					value: keys[1],
				},
			});
			return res.data.items.map((item: { id: string; name: string }) => ({
				label: item.name,
				value: item.id,
				data: item,
			}));
		},
		infinite: true,
		enabled: true,
	});
};
